import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'src/components/design-system/Button';
import ClearFilterButton from 'src/components/design-system/ClearFilterButton';
import PopUp from 'src/components/design-system/PopUp';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import { SearchItemFilterCategories, SearchPriceRanges } from 'src/constants/searchResultFilters';
import useGuardedAsyncCallback from 'src/hooks/useGuardedAsyncCallback';
import AddToFavoritesDropdown from 'src/sections/catalog_products/AddToFavoritesDropdown';
import { DropDownCategories } from 'src/types/generic';
import { useContextSelector } from 'use-context-selector';
import { SearchProductFiltersContext } from './SearchProductFiltersContext';
import { SearchProductSelectionsContext } from './SearchProductSelectionsContext';

interface SearchResultsToolbarActionsProps {
  rightContent?: ReactNode;
}

const SearchResultsToolbarActions = ({ rightContent }: SearchResultsToolbarActionsProps) => {
  const { t } = useTranslation();

  const isSupplierUser = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.isSupplierUser
  );
  const hasSelectedItems = useContextSelector(
    SearchProductSelectionsContext,
    (context) => context.hasSelectedItems
  );
  const folderOptions = useContextSelector(
    SearchProductSelectionsContext,
    (context) => context.folderOptions
  );
  const handleAddToFavorites = useContextSelector(
    SearchProductSelectionsContext,
    (context) => context.handleAddToFavorites
  );
  const isAddingToFavorites = useContextSelector(
    SearchProductSelectionsContext,
    (context) => context.isAddingToFavorites
  );
  const isUpdatingCart = useContextSelector(
    SearchProductSelectionsContext,
    (context) => context.isUpdatingCart
  );
  const clearFilters = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.clearFilters
  );
  const hasActiveFilters = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.hasActiveFilters
  );
  const handleSelectedItemsAddToCart = useContextSelector(
    SearchProductSelectionsContext,
    (context) => context.handleSelectedItemsAddToCart
  );
  const handleConfirm = useContextSelector(
    SearchProductSelectionsContext,
    (context) => context.handleConfirm
  );
  const priceRangeFilter = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.priceRangeFilter
  );
  const setPriceRangeFilter = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.setPriceRangeFilter
  );
  const itemFilter = useContextSelector(SearchProductFiltersContext, (context) => context.itemFilter);
  const setItemFilter = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.setItemFilter
  );
  const setDataState = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.setDataState
  );

  const [handleAddToCartClick, isAddToCartPending] = useGuardedAsyncCallback(
    handleSelectedItemsAddToCart
  );

  const isCartActionPending = isUpdatingCart || isAddToCartPending;
  const isAnyActionPending = isCartActionPending || isAddingToFavorites;

  const translatedPriceRanges = useMemo(
    () => SearchPriceRanges.map((range) => ({ ...range, label: t(range.label) })),
    [t]
  );

  const selectedPriceRange = useMemo(
    () =>
      translatedPriceRanges.find((range) => range.value === priceRangeFilter) ??
      translatedPriceRanges[0],
    [translatedPriceRanges, priceRangeFilter]
  );

  const translatedItemFilters = useMemo(
    () => SearchItemFilterCategories.map((cat) => ({ ...cat, label: t(cat.label) })),
    [t]
  );

  const translatedItemFilter = useMemo(
    () => (itemFilter ? { ...itemFilter, label: t(itemFilter.label) } : translatedItemFilters[0]),
    [itemFilter, t, translatedItemFilters]
  );

  const handlePriceRangeChange = useCallback(
    async (e: DropDownListChangeEvent) => {
      const { value } = e.value as { value: string };
      await handleConfirm(() => {
        setPriceRangeFilter(value);
        setDataState((prev) => ({ ...prev, skip: 0 }));
      });
    },
    [handleConfirm, setDataState, setPriceRangeFilter]
  );

  const handleItemFilterChange = useCallback(
    async (e: DropDownListChangeEvent) => {
      const value = e.value as unknown as DropDownCategories;
      await handleConfirm(() => {
        setItemFilter(value);
        setDataState((prev) => ({ ...prev, skip: 0 }));
      });
    },
    [handleConfirm, setDataState, setItemFilter]
  );

  return (
    <div className="flex w-full flex-row flex-wrap items-center gap-x-2 gap-y-2">
      <div className="flex min-w-0 flex-row flex-wrap items-center gap-1">
        <ClearFilterButton onClick={clearFilters} disabled={!hasActiveFilters || isAnyActionPending} />
        {!isSupplierUser ? (
          <>
            <AddToFavoritesDropdown
              folders={folderOptions}
              onAdd={handleAddToFavorites}
              disabled={!hasSelectedItems}
              isBusy={isAnyActionPending && !isAddingToFavorites}
              isLoading={isAddingToFavorites}
              size="lg"
            />
            <PopUp
              popupAlign={{ horizontal: 'center', vertical: 'top' }}
              anchorAlign={{ horizontal: 'center', vertical: 'bottom' }}
              className="max-h-[180px]"
              content={
                <div className="m-1 max-w-[350px]">
                  <PopUpContentWrapper
                    type="Information"
                    contents={[t('Please select at least one item to Add to Cart')]}
                  />
                </div>
              }
              show={!hasSelectedItems}
            >
              <Button
                color="success"
                size="lg"
                disabled={!hasSelectedItems || (isAnyActionPending && !isCartActionPending)}
                loading={isCartActionPending}
                onClick={handleAddToCartClick}
              >
                {t('Add to Cart')}
              </Button>
            </PopUp>
          </>
        ) : null}
      </div>
      <div className="ml-auto flex min-w-0 flex-row flex-wrap items-center justify-end gap-2">
        <DropDownList
          id="search-price-range-dropdown"
          className="k-picker-solid product-filter-dropdown"
          style={{ width: 200 }}
          data={translatedPriceRanges}
          textField="label"
          dataItemKey="value"
          value={selectedPriceRange}
          onChange={handlePriceRangeChange}
        />
          <DropDownList
          id="search-item-filter-dropdown"
          className="k-picker-solid product-filter-dropdown"
          style={{ width: 200 }}
          data={translatedItemFilters}
          textField="label"
          dataItemKey="value"
          value={translatedItemFilter}
          onChange={handleItemFilterChange}
        />
        {rightContent}
      </div>
    </div>
  );
};

export default SearchResultsToolbarActions;
